import { Component } from "react";
import { ExportFormatType, ExportGapFilterType, LocationStatuses, PractitionerAtLocationStatuses, PractitionerStatuses, ReportSettingsType, ExportProviderAvailabilityType, NppesStatuses } from "../enumerations";
import { ProviderNetworkType } from "@app/projects/enumerators";
import { ExportStatusReportModel, RegionSetFilter, ReportSettingsModel } from "../exportModels";
import ExportService from "../exportService";
import * as helpers from "./exportPanelBodyHelpers";
import { SelectFormat, AccuracyStatusFilter } from "./filters";
import GenerateBody from "./generate-body";
import { ReportPanelBodyBaseProps, ReportPanelBodyBaseState } from "./reportPanelBodyBase";
import * as toast from "./toast";
import { ReportSettingsSaveAction, ReportSettingsBody } from "./report-settings";
import { ReportSettingsProps, ReportSettingsState } from "./report-settings/reportSettings";
import { isEqual } from "lodash";

type StatusReportPanelBodyProps =
  ReportPanelBodyBaseProps &
  ReportSettingsProps & {
    activeNetworkType: ProviderNetworkType;
    inspectionBand: string;
    inspectionUids: string[];
    projectId: number;
    providerAvailabilityType: ExportProviderAvailabilityType;
    providerIds: number[];
    runConfigurationId: number;
    specialtyGroupCodes: string[];
  };

interface StatusReportPanelBodyState extends ReportPanelBodyBaseState, ReportSettingsState {
  regionSetFilter: RegionSetFilter;
  providerFilter: {
    filterType: ProviderNetworkType;
    providerAvailabilityType: ExportProviderAvailabilityType;
  };
  practitionerStatusRowIds: PractitionerStatuses[];
  nppesStatusRowIds: NppesStatuses[];
  locationStatusRowIds: LocationStatuses[];
  practitionerAtLocationStatusRowIds: PractitionerAtLocationStatuses[];
  sharedUsers: number[];
}

export default class StatusReportPanelBody extends Component<StatusReportPanelBodyProps, StatusReportPanelBodyState> {
  private readonly exportService = new ExportService();

  constructor(props: StatusReportPanelBodyProps) {
    super(props);

    this.state = {
      ...this.getInitialBodyState(),
      ...this.getInitialReportSettingsState()
    };
  }

  public componentDidUpdate(prevProps: StatusReportPanelBodyProps) {
    if (!isEqual(prevProps.reportSettingsList, this.props.reportSettingsList)) {
      const reportSettingsList = this.props.reportSettingsList;
      if (reportSettingsList) {
        const currentReportSettings = reportSettingsList.find(rs => rs.isDefault);
        if (currentReportSettings)
          this.handleParseReportSettingsJsonToState(currentReportSettings.reportSettingsJson);
        this.setState({
          currentReportSettings,
          reportSettingsList
        })
      }
    }
  }

  public componentWillUnmount() {
    toast.dismiss();
  }

  public render() {
    const isAccuracyModule = this.props.match.params.module === "accuracy";

    return (
      <>
        {isAccuracyModule &&
          <ReportSettingsBody
            buildReportSettingsModel={this.buildReportSettingsModel}
            createReportSettingsNameInputDisabled={this.state.createReportSettingsNameInputDisabled}
            currentReportSettings={this.state.currentReportSettings}
            isSelectionValid={this.state.isSelectionValid}
            parseReportSettingsJsonToState={this.handleParseReportSettingsJsonToState}
            reportSettingsHasChanges={this.state.reportSettingsHasChanges}
            reportSettingsHasInvalidChanges={this.state.reportSettingsHasInvalidChanges}
            reportSettingsList={this.state.reportSettingsList}
            saveClicked={this.state.saveClicked}
            setCreateReportSettingsNameInputDisabled={this.setCreateReportSettingsNameInputDisabled}
            setCurrentReportSettings={this.setCurrentReportSettings}
            setReportSettingsIsSelectionValid={this.setReportSettingsIsSelectionValid}
            setReportSettingsHasChanges={this.setReportSettingsHasChanges}
            setReportSettingsHasInvalidChanges={this.setReportSettingsHasInvalidChanges}
            setReportSettingsList={this.setReportSettingsList}
            setSaveClicked={this.setSaveClicked}
          />
        }
        <div className="row">
          <div className="col-12 col-md-8">
            <AccuracyStatusFilter
              disabled={this.props.disableRegionFilters}
              practitionerStatusRowIds={this.state.practitionerStatusRowIds}
              nppesStatusRowIds={this.state.nppesStatusRowIds}
              locationStatusRowIds={this.state.locationStatusRowIds}
              practitionerAtLocationStatusRowIds={this.state.practitionerAtLocationStatusRowIds}
              onPractitionerStatusChange={this.handlePractitionerStatusChange}
              onLocationStatusChange={this.handleLocationStatusChange}
              onPractitionerAtLocationStatusChange={this.handlePractitionerAtLocationStatusChange}
              onNppesStatusChange={this.handleNppesStatusChange}
            />
          </div>
          <div className="col-12 col-sm-4">
            <SelectFormat outputFormat={this.state.outputFormat} onOutputFormatChange={this.handleOutputFormatChange} excelDisabled={false} exportType="specialty-aggregate" />
          </div>
        </div>
        {isAccuracyModule &&
          <ReportSettingsSaveAction
            currentReportSettings={this.state.currentReportSettings}
            isSelectionValid={this.state.isSelectionValid}
            reportSettingsHasChanges={this.state.reportSettingsHasChanges}
            reportSettingsHasInvalidChanges={this.state.reportSettingsHasInvalidChanges}
            reportSettingsList={this.state.reportSettingsList}
            saveClicked={this.state.saveClicked}
            setCurrentReportSettings={this.setCurrentReportSettings}
            setCreateReportSettingsNameInputDisabled={this.setCreateReportSettingsNameInputDisabled}
            setReportSettingsHasChanges={this.setReportSettingsHasChanges}
            setReportSettingsList={this.setReportSettingsList}
            setSaveClicked={this.setSaveClicked}
            stringifyStateReportSettings={this.stringifyStateReportSettings}
          />
        }
        <GenerateBody
          {...this.props}
          emailOnSuccess={this.state.emailOnSuccess}
          reportName={this.state.reportName}
          onGenerateClick={this.handleGenerateClick}
          onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
          onReportNameChange={this.handleReportNameChange}
          isLoading={this.state.loadingReportSettingsJSONStrings && this.state.loadingReportSettingsJSONStrings.indexOf(helpers.stringifyStateReportSettings(this.state)) > -1}
          isValid={(this.state.regionSetFilter.includeActive || this.state.regionSetFilter.includePending || this.state.regionSetFilter.includeApproved)}
          showSharedUsers={true}
          sharedUsers={this.state.sharedUsers}
          updateSelectedSharedUsers={this.handleSharedUsersChange}
          exportType="AccuracyStatusReport"
          projectId={this.props.projectId}
          networkAssessmentAnalysisType={this.state.regionSetFilter.networkAssessmentAnalysisType}
        />
      </>
    );
  }

  private getInitialBodyState = (): StatusReportPanelBodyState => {
    return {
      emailOnSuccess: true,
      loadingReportSettingsJSONStrings: [],
      locationStatusRowIds: this.setDefaultStatusRowIds(LocationStatuses),
      nppesStatusRowIds: this.setDefaultStatusRowIds(NppesStatuses),
      outputFormat: ExportFormatType.CSV,
      practitionerAtLocationStatusRowIds: this.setDefaultStatusRowIds(PractitionerAtLocationStatuses),
      practitionerStatusRowIds: this.setDefaultStatusRowIds(PractitionerStatuses),
      providerFilter: {
        filterType: this.props.activeNetworkType || ProviderNetworkType.Network,
        providerAvailabilityType: this.props.comparedRunConfigurationId !== null ? ExportProviderAvailabilityType.Changed : this.props.providerAvailabilityType,
      },
      regionSetFilter: {
        filterType: ExportGapFilterType.All,
        includeActive: true,
        includeApproved: true,
        includePending: true,
        networkAssessmentAnalysisType: this.props.initialNetworkAssessmentAnalysisType,
      },
      reportName: helpers.reportName("ProviderStatus"),
      sharedUsers: [],
    } as StatusReportPanelBodyState;
  }

    private setDefaultStatusRowIds(statuses: { [n: number]: string }) {
    const defaults: string[] = [];
    Object.values(statuses).map((v) => {
      defaults.push(v)
    })

    return defaults;
  }

  private handlePractitionerStatusChange = (id: PractitionerStatuses) => {
    const currentRowTypeIds = [...this.state.practitionerStatusRowIds];

    if (currentRowTypeIds.indexOf(id) > -1) {
      currentRowTypeIds.splice(currentRowTypeIds.indexOf(id), 1);
    } else {
      currentRowTypeIds.push(id);
    }

    this.setState((prevState) => ({
      ...prevState,
      practitionerStatusRowIds: currentRowTypeIds,
      reportSettingsHasChanges: true,
    }));
  }

  private handleNppesStatusChange = (id: NppesStatuses) => {
    const currentRowTypeIds = [...this.state.nppesStatusRowIds];

    if (currentRowTypeIds.indexOf(id) > -1) {
      currentRowTypeIds.splice(currentRowTypeIds.indexOf(id), 1);
    } else {
      currentRowTypeIds.push(id);
    }

    this.setState((prevState) => ({
      ...prevState,
      nppesStatusRowIds: currentRowTypeIds,
      reportSettingsHasChanges: true,
    }));
  }

  private handleLocationStatusChange = (id: LocationStatuses) => {
    const currentRowTypeIds = [...this.state.locationStatusRowIds];

    if (currentRowTypeIds.indexOf(id) > -1) {
      currentRowTypeIds.splice(currentRowTypeIds.indexOf(id), 1);
    } else {
      currentRowTypeIds.push(id);
    }

    this.setState((prevState) => ({
      ...prevState,
      locationStatusRowIds: currentRowTypeIds,
      reportSettingsHasChanges: true,
    }));
  }

  private handlePractitionerAtLocationStatusChange = (id: PractitionerAtLocationStatuses) => {
    const currentRowTypeIds = [...this.state.practitionerAtLocationStatusRowIds];

    if (currentRowTypeIds.indexOf(id) > -1) {
      currentRowTypeIds.splice(currentRowTypeIds.indexOf(id), 1);
    } else {
      currentRowTypeIds.push(id);
    }

    this.setState((prevState) => ({
      ...prevState,
      practitionerAtLocationStatusRowIds: currentRowTypeIds,
      reportSettingsHasChanges: true,
    }));
  }

  private getInitialReportSettingsState = (): ReportSettingsState => {
    return {
      createReportSettingsNameInputDisabled: true,
      currentReportSettings: undefined,
      isSelectionValid: true,
      reportSettingsHasChanges: false,
      reportSettingsHasInvalidChanges: false,
      reportSettingsList: [],
      saveClicked: false,
    } as ReportSettingsState;
  }

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    this.setState({ outputFormat, reportSettingsHasChanges: true });
  }

  private handleGenerateClick = async () => {
    const stateReportSettingsJSON = helpers.stringifyStateReportSettings(this.state);
    this.setState((prevState) => ({
      ...prevState,
      loadingReportSettingsJSONStrings: [
        ...prevState.loadingReportSettingsJSONStrings,
        stateReportSettingsJSON,
      ],
    }));

    switch (this.props.match.params.module) {
      case "accuracy":
        await this.generateExportAccuracyStatusReportModel();
        break;
      default:
        break;
    }

    toast.toastInfo(this.state.reportName);
  }

  // Accuracy
  private generateExportAccuracyStatusReportModel = async () => {
    const statusModel: ExportStatusReportModel = {
      countyFilters: this.props.countyFilters,
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      inspectionBand: this.props.inspectionBand,
      inspectionUids: this.props.inspectionUids,
      locationStatuses: this.state.locationStatusRowIds,
      networkAssessmentAnalysisType: this.state.regionSetFilter.networkAssessmentAnalysisType,
      nppesStatuses: this.state.nppesStatusRowIds,
      practitionerAtLocationStatuses: this.state.practitionerAtLocationStatusRowIds,
      practitionerStatuses: this.state.practitionerStatusRowIds,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      providerIds: this.props.providerIds,
      providerNetworkType: this.state.providerFilter.filterType,
      runAsynchronous: true,
      sharedUsers: this.state.sharedUsers,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
      stateFilters: this.props.stateFilters,
    };
    await this.exportService.postExportAccuracyStatusReport(statusModel);
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess, reportSettingsHasChanges: true });
  }

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  }

  // Report Settings
  private stringifyStateReportSettings = (): string => {
    return helpers.stringifyStateReportSettings({
      emailOnSuccess: this.state.emailOnSuccess,
      loadingReportSettingsJSONStrings: this.state.loadingReportSettingsJSONStrings,
      locationStatusRowIds: this.state.locationStatusRowIds,
      nppesStatusRowIds: this.state.nppesStatusRowIds,
      outputFormat: this.state.outputFormat,
      practitionerAtLocationStatusRowIds: this.state.practitionerAtLocationStatusRowIds,
      practitionerStatusRowIds: this.state.practitionerStatusRowIds,
      regionSetFilter: this.state.regionSetFilter,
      sharedUsers: this.state.sharedUsers,
    } as StatusReportPanelBodyState);
  }

  private buildReportSettingsModel = (isDefault: boolean, name: string): ReportSettingsModel => {
    const reportSettingsJson = this.stringifyStateReportSettings();
    return {
      isDefault,
      name,
      reportSettingsJson,
      type: this.reportSettingsType,
    } as ReportSettingsModel;
  }

  private get reportSettingsType() {
    switch (this.props.match.params.module) {
      case "accuracy":
        return ReportSettingsType.AccuracyStatusReport;
      default:
        return ReportSettingsType.Unknown;
    }
  }

  private handleParseReportSettingsJsonToState = (reportSettingsJson: string) => {
    const settings = helpers.tryParseReportSettings(reportSettingsJson);
    if (settings) {
      this.setReportSettingsIsSelectionValid(true);
      this.setState((prevState) => ({
        ...prevState,
        ...settings,
      }));
    }
    else {
      const initialBodyState = this.getInitialBodyState();
      this.setReportSettingsIsSelectionValid(false);
      this.setState((prevState) => ({
        ...prevState,
        ...initialBodyState
      }));
    }
  }

  private setCurrentReportSettings = (currentReportSettings: ReportSettingsModel) => {
    this.setState((prevState) => ({
      ...prevState,
      currentReportSettings,
    }));
  }

  private setReportSettingsList = (rsList: ReportSettingsModel[]) => {
    const reportSettingsList = this.props.sortedReportSettings(rsList, "name");
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsList,
    }))
  }

  private setReportSettingsHasChanges = (reportSettingsHasChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges,
    }))
  }

  private setReportSettingsHasInvalidChanges = (reportSettingsHasInvalidChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasInvalidChanges,
    }))
  }

  private setReportSettingsIsSelectionValid = (isSelectionValid: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      isSelectionValid,
    }))
  }

  private setCreateReportSettingsNameInputDisabled = (createReportSettingsNameInputDisabled: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      createReportSettingsNameInputDisabled,
    }))
  }

  private setSaveClicked = (saveClicked: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      saveClicked,
    }))
  }

  private handleSharedUsersChange = (sharedUsers: number[]) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      sharedUsers: sharedUsers,
    }));
  }
}